import AsyncStorage from '@react-native-async-storage/async-storage';

const employeeWelcomeKey = (userId: string) => `onboarding.employee_welcome.v1.${userId}`;
const ownerChecklistKey = (userId: string) => `onboarding.owner_checklist.v1.${userId}`;
/** Dismiss timestamp (ISO) or legacy `'1'` (permanent). */
const storeActivationKey = (userId: string) => `onboarding.store_activation.v1.${userId}`;
/** Opt-in: user tapped «Configurar mi tienda». Cleared on dismiss. */
const storeActivationSetupKey = (userId: string) => `onboarding.store_activation.setup.v1.${userId}`;
const clientesRenameTipKey = (userId: string) => `onboarding.clientes_rename_tip.v1.${userId}`;
/** Dismiss timestamp (ISO) or legacy `'1'` (permanent). */
const weekOneSetupKey = (userId: string) => `onboarding.week_one_setup.v1.${userId}`;
const weekOneTeamSkippedKey = (userId: string) => `onboarding.week_one_setup.team_skip.v1.${userId}`;
const weekOneMpSkippedKey = (userId: string) => `onboarding.week_one_setup.mp_skip.v1.${userId}`;
const weekOnePrinterSkippedKey = (userId: string) => `onboarding.week_one_setup.printer_skip.v1.${userId}`;
const weekOneEssentialsSavedKey = (userId: string, branchId: string) =>
  `onboarding.week_one_setup.essentials.v1.${userId}.${branchId}`;
const orgLogoNudgeKey = (userId: string, organizationId: string) =>
  `onboarding.org_logo_nudge.v1.${userId}.${organizationId}`;

/** Reappear nudge after this cooldown if the store is still unpublished. */
export const STORE_ACTIVATION_DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
export const WEEK_ONE_SETUP_DISMISS_MS = STORE_ACTIVATION_DISMISS_MS;

export async function isEmployeeWelcomeDismissed(userId: string): Promise<boolean> {
  const v = await AsyncStorage.getItem(employeeWelcomeKey(userId));
  return v === '1';
}

export async function dismissEmployeeWelcome(userId: string): Promise<void> {
  await AsyncStorage.setItem(employeeWelcomeKey(userId), '1');
}

export async function isOwnerChecklistDismissed(userId: string): Promise<boolean> {
  const v = await AsyncStorage.getItem(ownerChecklistKey(userId));
  return v === '1';
}

export async function dismissOwnerChecklist(userId: string): Promise<void> {
  await AsyncStorage.setItem(ownerChecklistKey(userId), '1');
}

/**
 * True while the store-activation offer should stay hidden.
 * New dismisses store an ISO timestamp and reappear after 7 days.
 * Legacy `'1'` stays permanent (no spam for users who already dismissed forever).
 */
export async function isStoreActivationDismissed(
  userId: string,
  nowMs: number = Date.now(),
): Promise<boolean> {
  const v = await AsyncStorage.getItem(storeActivationKey(userId));
  if (!v) return false;
  if (v === '1') return true;
  const ts = Date.parse(v);
  if (Number.isNaN(ts)) return false;
  return nowMs - ts < STORE_ACTIVATION_DISMISS_MS;
}

export async function dismissStoreActivation(userId: string): Promise<void> {
  await AsyncStorage.setItem(storeActivationKey(userId), new Date().toISOString());
  await AsyncStorage.removeItem(storeActivationSetupKey(userId));
}

export async function isStoreActivationSetupStarted(userId: string): Promise<boolean> {
  const v = await AsyncStorage.getItem(storeActivationSetupKey(userId));
  return v === '1';
}

export async function startStoreActivationSetup(userId: string): Promise<void> {
  await AsyncStorage.setItem(storeActivationSetupKey(userId), '1');
}

export async function isClientesRenameTipDismissed(userId: string): Promise<boolean> {
  const v = await AsyncStorage.getItem(clientesRenameTipKey(userId));
  return v === '1';
}

export async function dismissClientesRenameTip(userId: string): Promise<void> {
  await AsyncStorage.setItem(clientesRenameTipKey(userId), '1');
}

export async function isWeekOneSetupDismissed(
  userId: string,
  nowMs: number = Date.now(),
): Promise<boolean> {
  const v = await AsyncStorage.getItem(weekOneSetupKey(userId));
  if (!v) return false;
  if (v === '1') return true;
  const ts = Date.parse(v);
  if (Number.isNaN(ts)) return false;
  return nowMs - ts < WEEK_ONE_SETUP_DISMISS_MS;
}

export async function dismissWeekOneSetup(userId: string): Promise<void> {
  await AsyncStorage.setItem(weekOneSetupKey(userId), new Date().toISOString());
}

export async function isWeekOneTeamSkipped(userId: string): Promise<boolean> {
  const v = await AsyncStorage.getItem(weekOneTeamSkippedKey(userId));
  return v === '1';
}

export async function skipWeekOneTeamStep(userId: string): Promise<void> {
  await AsyncStorage.setItem(weekOneTeamSkippedKey(userId), '1');
}

export async function isWeekOneMpSkipped(userId: string): Promise<boolean> {
  const v = await AsyncStorage.getItem(weekOneMpSkippedKey(userId));
  return v === '1';
}

export async function skipWeekOneMpStep(userId: string): Promise<void> {
  await AsyncStorage.setItem(weekOneMpSkippedKey(userId), '1');
}

export async function isWeekOnePrinterSkipped(userId: string): Promise<boolean> {
  const v = await AsyncStorage.getItem(weekOnePrinterSkippedKey(userId));
  return v === '1';
}

export async function skipWeekOnePrinterStep(userId: string): Promise<void> {
  await AsyncStorage.setItem(weekOnePrinterSkippedKey(userId), '1');
}

export async function isWeekOneEssentialsSaved(userId: string, branchId: string): Promise<boolean> {
  const v = await AsyncStorage.getItem(weekOneEssentialsSavedKey(userId, branchId));
  return v === '1';
}

export async function markWeekOneEssentialsSaved(userId: string, branchId: string): Promise<void> {
  await AsyncStorage.setItem(weekOneEssentialsSavedKey(userId, branchId), '1');
}

/** Dismiss timestamp (ISO) or legacy `'1'` (permanent). */
const maturityWeeklySummaryKey = (userId: string) => `onboarding.maturity_weekly_summary.v1.${userId}`;
const customerMaturityNudgeKey = (userId: string) => `onboarding.maturity_fiado_nudge.v1.${userId}`;

export const MATURITY_WEEKLY_SUMMARY_DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

export async function isMaturityWeeklySummaryDismissed(
  userId: string,
  nowMs: number = Date.now(),
): Promise<boolean> {
  const v = await AsyncStorage.getItem(maturityWeeklySummaryKey(userId));
  if (!v) return false;
  if (v === '1') return true;
  const ts = Date.parse(v);
  if (Number.isNaN(ts)) return false;
  return nowMs - ts < MATURITY_WEEKLY_SUMMARY_DISMISS_MS;
}

export async function dismissMaturityWeeklySummary(userId: string): Promise<void> {
  await AsyncStorage.setItem(maturityWeeklySummaryKey(userId), new Date().toISOString());
}

export async function isCustomerMaturityNudgeDismissed(userId: string): Promise<boolean> {
  const v = await AsyncStorage.getItem(customerMaturityNudgeKey(userId));
  return v === '1';
}

export async function dismissCustomerMaturityNudge(userId: string): Promise<void> {
  await AsyncStorage.setItem(customerMaturityNudgeKey(userId), '1');
}

export async function isOrganizationLogoNudgeDismissed(
  userId: string,
  organizationId: string,
): Promise<boolean> {
  const v = await AsyncStorage.getItem(orgLogoNudgeKey(userId, organizationId));
  return v === '1';
}

export async function dismissOrganizationLogoNudge(
  userId: string,
  organizationId: string,
): Promise<void> {
  await AsyncStorage.setItem(orgLogoNudgeKey(userId, organizationId), '1');
}

const onlineOperatingKey = (userId: string) => `onboarding.online_operating.v1.${userId}`;
const onlineOperatingInboxKey = (userId: string) => `onboarding.online_operating.inbox.v1.${userId}`;
const onlineOrderSoundKey = (userId: string) => `onboarding.online_order_sound.v1.${userId}`;

export const ONLINE_OPERATING_DISMISS_MS = STORE_ACTIVATION_DISMISS_MS;

export async function isOnlineOperatingDismissed(
  userId: string,
  nowMs: number = Date.now(),
): Promise<boolean> {
  const v = await AsyncStorage.getItem(onlineOperatingKey(userId));
  if (!v) return false;
  if (v === '1') return true;
  const ts = Date.parse(v);
  if (Number.isNaN(ts)) return false;
  return nowMs - ts < ONLINE_OPERATING_DISMISS_MS;
}

export async function dismissOnlineOperating(userId: string): Promise<void> {
  await AsyncStorage.setItem(onlineOperatingKey(userId), new Date().toISOString());
}

export async function isOnlineOperatingInboxSeen(userId: string): Promise<boolean> {
  const v = await AsyncStorage.getItem(onlineOperatingInboxKey(userId));
  return v === '1';
}

export async function markOnlineOperatingInboxSeen(userId: string): Promise<void> {
  await AsyncStorage.setItem(onlineOperatingInboxKey(userId), '1');
}

/** Default off — usuario activa sonido en card B4. */
export async function isOnlineOrderSoundEnabled(userId: string): Promise<boolean> {
  const v = await AsyncStorage.getItem(onlineOrderSoundKey(userId));
  return v === '1';
}

export async function setOnlineOrderSoundEnabled(userId: string, enabled: boolean): Promise<void> {
  if (enabled) {
    await AsyncStorage.setItem(onlineOrderSoundKey(userId), '1');
  } else {
    await AsyncStorage.removeItem(onlineOrderSoundKey(userId));
  }
}
